import {
  CloseCircleOutlined,
  EnterOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import { useState } from 'react';
import styled from 'styled-components';

import { CenterFlex, Flex, ShortcutStyle } from '@/components/base';
import Input from '@/components/Inputs/Input';
import Typo from '@/components/Typography/Typo';
import useColor from '@/hooks/domain/useColor';
import useAutoResizeInput from '@/hooks/pure/useAutoResizeInput';
import { useToast } from '@/stores/pure/ToastStore';
import { FEEDBACK_COLUMNS } from '@/types/filterKeyConst';
import { STRUCTURED_FILTER_OPERATOR } from '@/types/structuredFilterConst';
import keymap from '@/utils/keymap';
import { isEmptyString } from '@/utils/util';

import FieldItemOperatorSelector from './FieldItemOperatorSelector';

type Props = {
  /**
   * @description 필터 이름
   * tracking 용도로 사용됨
   */
  name: string;
  keywordFilter: StructuredFilterStringNumberValueSet | null;
  allowSpace?: boolean;
  onChange: (filter: StructuredFilterStringNumberValueSet | null) => void;
  restriction?: 'include' | 'exclude';
};

const InlineKeywordFilterV2 = (props: Props) => {
  const { keywordFilter, onChange } = props;

  return (
    <Style g={8} w="wrap" a="center">
      {keywordFilter && keywordFilter.values.length > 0 && (
        <OperatorWrapper>
          <FieldItemOperatorSelector
            dropdownKey={`search-keyword-operator-selector-${FEEDBACK_COLUMNS.SNIPPET}-${props.name}`}
            operator={keywordFilter.op}
            hasMultiValues={keywordFilter.values.length > 1}
            isMultiValueField={true}
            restriction={props.restriction}
            onChange={(operator) => {
              onChange({
                ...keywordFilter,
                op: operator,
              });
            }}
          />
        </OperatorWrapper>
      )}
      <Flex g={8} f={1} w="wrap" a="center">
        {keywordFilter &&
          keywordFilter.values.map((keyword) => (
            <KeywordItem
              key={keyword}
              keyword={keyword.toString()}
              onRemove={() => {
                const values = keywordFilter.values.filter(
                  (k) => k !== keyword,
                );

                if (values.length === 0) {
                  onChange(null);
                } else {
                  onChange({
                    ...keywordFilter,
                    values: values,
                  });
                }
              }}
            />
          ))}
        <AddKeyword {...props} />
      </Flex>
    </Style>
  );
};

const Style = styled(Flex)`
  min-height: 32px;
  border: 1px solid ${(props) => props.theme.colors.gray400};
  background-color: ${(props) => props.theme.colors.gray100};
  padding: 4px 16px;
  border-radius: 16px;
`;

const OperatorWrapper = styled(Flex)`
  height: 100%;
  transition: background-color 0.2s ease-in-out;
  cursor: pointer;
  padding: 0px 4px;
  &:hover {
    background-color: ${(props) => props.theme.colors.gray200};
  }
`;

const AddKeyword = (props: Props) => {
  const addToast = useToast();
  const { keywordFilter, onChange } = props;
  const [isAdding, setIsAdding] = useState<boolean>(false);
  const color = useColor();
  const { inputRef, inputValue, inputWidth, handleChange } =
    useAutoResizeInput('');

  if (isAdding)
    return (
      <Flex a="center" g={8}>
        <AddingInput
          ref={inputRef}
          autoFocus={true}
          value={inputValue}
          placeholder="Enter to add keyword"
          onChange={(e) => {
            const text = props.allowSpace
              ? e.target.value
              : e.target.value.trim();
            handleChange(text);
          }}
          style={{ width: inputWidth, minWidth: 140 }}
          onBlur={(e) => {
            if (isEmptyString(inputValue)) {
              handleChange('');
              setIsAdding(false);
              return;
            }
            if (keywordFilter) {
              onChange({
                ...keywordFilter,
                values: Array.from(
                  new Set([...keywordFilter.values, inputValue]),
                ),
              });
            } else {
              onChange({
                op: (function () {
                  switch (props.restriction) {
                    case 'include':
                      return STRUCTURED_FILTER_OPERATOR.OR;
                    case 'exclude':
                      return STRUCTURED_FILTER_OPERATOR.NOR;
                    default:
                      return STRUCTURED_FILTER_OPERATOR.OR;
                  }
                })(),
                values: [inputValue],
              });
            }
            handleChange('');
            setIsAdding(false);
          }}
          onKeyDown={(e) => {
            if (!props.allowSpace && keymap.isSpace(e)) {
              e.preventDefault();
              addToast('Space is not allowed', 'fail');
              return;
            }
            if (keymap.isEnter(e)) {
              e.preventDefault();
              if (isEmptyString(inputValue)) {
                handleChange('');
                setIsAdding(false);
                return;
              }
              if (keywordFilter) {
                onChange({
                  ...keywordFilter,
                  values: Array.from(
                    new Set([...keywordFilter.values, inputValue]),
                  ),
                });
              } else {
                onChange({
                  op: (function () {
                    switch (props.restriction) {
                      case 'include':
                        return STRUCTURED_FILTER_OPERATOR.OR;
                      case 'exclude':
                        return STRUCTURED_FILTER_OPERATOR.NOR;
                      default:
                        return STRUCTURED_FILTER_OPERATOR.OR;
                    }
                  })(),
                  values: [inputValue],
                });
              }
              handleChange('');

              const target = e.currentTarget;
              setTimeout(() => {
                target.focus();
              }, 100);
            }
            if (keymap.isEscape(e)) {
              setIsAdding(false);
            }
          }}
        />
        <ShortcutStyle outlined>
          <EnterOutlined />
        </ShortcutStyle>
      </Flex>
    );
  return (
    <CenterFlex height="100%">
      <PlusOutlined
        style={{ fontSize: 14, color: color.gray700 }}
        onClick={() => {
          setIsAdding(true);
        }}
      />
    </CenterFlex>
  );
};

const AddingInput = styled(Input)`
  border: none;
  padding: 0;
  &:hover {
    border: none;
    background-color: transparent;
  }
  &:focus {
    border: none;
  }
`;

type ItemProps = {
  keyword: string;
  onRemove: () => void;
};

const KeywordItem = (itemProps: ItemProps) => {
  const { keyword, onRemove } = itemProps;
  return (
    <KeywordItemStyle>
      <Typo type="body3">{keyword}</Typo>
      <CloseCircleOutlined style={{ fontSize: 12 }} onClick={onRemove} />
    </KeywordItemStyle>
  );
};

const KeywordItemStyle = styled(Flex).attrs({
  a: 'center',
  g: 4,
})`
  width: fit-content;
  height: 20px;
  padding: 4px 8px;
  color: ${(props) => props.theme.colors.gray700};
  background-color: ${(props) => props.theme.colors.gray200};
  border-radius: 16px;
`;

export default InlineKeywordFilterV2;
